import {
	Route,
	createBrowserRouter,
	createRoutesFromElements,
	RouterProvider,
	Outlet,
} from "react-router-dom";
import "react-loading-skeleton/dist/skeleton.css";
import React, { Suspense, useEffect } from 'react';
import { AppProvider } from "@contexts/AppProvider";
import { FEModules, FEModulesObj } from "@utils/moduleList";
import { useFirebaseHook, useScreenViewEventsHandler, useSetFirebaseUserProperties } from "@utils/firebaseUtils";
import { useMsaEventSubscription } from "@app/hooks/useMsaEventSubscription";
import { useMapSaveStatusPolling } from "@app/hooks/useMapSaveStatusPolling";
import { useLandCoverChangeAutoTrigger } from "@app/hooks/useLandCoverChangeAutoTrigger";

import useTabsHook from "@utils/useTabsHook";
import ProtectedLayout from "./ProtectedLayout";
import Header from "@components/header/Header";
import Login from "@pages/accessManagement/login/Login";
import SelectProject from "@pages/selectProject/SelectProject";
import ActivityBuilder from "@pages/activityBuilder/ActivityBuilder";
import ProjectContainer from "@pages/project/ProjectContainer";
import ProjectDescriptionContainer from "@pages/project/description/ProjectDescriptionContainer";
import ProjectActivitiesContainer from "@pages/project/activities/ProjectActivitiesContainer";
import ProjectResultsContainer from "@pages/project/results/ProjectResultsContainer";
import SetupAssistantContainer from "@pages/project/activities/setupAssistant/SetupAssistantContainer";
import NewBiodiversityActivityPage from "@pages/project/activities/NewBiodiversityActivityPage";
import EditActivityContainer from "@pages/activity/EditActivityContainer";
import AuthLayout from "@pages/accessManagement/AuthLayout";
import Register from "@pages/accessManagement/register/Register";
import TermAndConditions from "@pages/accessManagement/TermAndConditions";
import ResetPassword from "@pages/accessManagement/resetPassword/ResetPassword";
import Provider from "./Provider";
import LoadingFallback from "@components/loading/LoadingFallback";
import EditActivityCombined from "@pages/activity/EditActivityCombined";
import { Footer } from "@components/footer/Footer";

import AnnualCroplandModule from "@pages/activityBuilder/modules/annualCropland/AnnualCroplandModule";
import LandUseChangeModule from "@pages/activityBuilder/modules/landUseChanges/LandUseChangeModule";
import LargeFisheriesModule from "@pages/activityBuilder/modules/largeFisheries/LargeFisheriesModule";
import SmallFisheriesModule from "@pages/activityBuilder/modules/smallFisheries/SmallFisheriesModule";
import GrasslandManagementModule from "@pages/activityBuilder/modules/grasslandManagement/GrasslandManagementModule";
import PerennialCropland from "@pages/activityBuilder/modules/perennialCropland/PerennialCroplandModule";
import ActivityCompleteStep from "@pages/activityBuilder/ActivityCompleteStep";
import Livestock from "@pages/activityBuilder/modules/livestockManagement/LivestockManagementModule";
import FloodedRice from "@pages/activityBuilder/modules/floodedRice/FloodedRiceModule";
import AquacultureModule from "@pages/activityBuilder/modules/aquaculture/AquacultureModule";
import NoMatch404 from "@pages/accessManagement/NoMatch404";
import InputsModule from "@pages/activityBuilder/modules/inputs/InputsModule";
import ForestManagement from "@pages/activityBuilder/modules/forestManagement/ForestManagementModule";
import SetAside from "@pages/activityBuilder/modules/setAside/SetAsideModule";
import Waterbodies from "@pages/activityBuilder/modules/waterbodies/WaterbodiesModule";
import OtherLand from "@pages/activityBuilder/modules/otherLand/OtherLandModule";
import Energy from "@pages/activityBuilder/modules/energy/EnergyModule";
import OrganicSoil from "@pages/activityBuilder/modules/organicSoil/OrganicSoilModule";
import Settlements from "@pages/activityBuilder/modules/settlements/SettlementsModule";
import Irrigation from "@pages/activityBuilder/modules/irrigation/IrrigationModule";
import CoastalWetlands from "@pages/activityBuilder/modules/coastalWetlands/CoastalWetlandsModule";
import Packaging from "@pages/activityBuilder/modules/packaging/PackagingModule";
import Transport from "@pages/activityBuilder/modules/transport/TransportModule";
import Storages from "@pages/activityBuilder/modules/storage/StorageModule";
import ErrorBoundary from "@components/ErrorBoundary";
import Processing from "@pages/activityBuilder/modules/processing/ProcessingModule";
import PublicLayout from "./PublicLayout";
import HihInvestmentForum from "@pages/hihInvestmentForum/HihInvestmentForum";
import BintactBuilder from "@pages/bintactBuilder/BintactBuilder";
import HelpCenter from "@pages/helpCenter/HelpCenter";
import HelpCenterHome from "@pages/helpCenter/HelpCenterHome";
import HelpCenterFaqs from "@pages/helpCenter/sections/HelpCenterFaqs";
import HelpCenterTraining from "@pages/helpCenter/sections/HelpCenterTraining";
import HelpCenterPublications from "@pages/helpCenter/sections/HelpCenterPublications";

const RouteOutlet = () => <Outlet />;

function App() {
	// Create module routes once
	const moduleRoutes = Object.entries(FEModulesObj).map(([key, moduleObj]) => {
		const module = Number(key) as FEModules;
		return moduleObj.path ? (
			<Route
				key={moduleObj.path}
				path={moduleObj.path}
				element={getModuleComponent(module)}
			/>
		) : null;
	});

	// Define shared routes that appear in both protected and public contexts
	const createSharedRoutes = (prefix: string) => (
		<>
			<Route
				id={`${prefix}-builder`}
				path="builder/:activityId"
				element={
					<ActivityBuilder />
				}
			>
				{moduleRoutes.map((route, index) => route ? React.cloneElement(route, { id: `${prefix}-module-${index}` }) : null)}
			</Route>
			<Route
				id={`${prefix}-bintact-builder`}
				path="bintact-builder/:projectId/:activityId?"
				element={
					<BintactBuilder />
				}
			/>
			<Route id={`${prefix}-project`} path="project/:projectId" element={<ProjectContainer />}>
				<Route id={`${prefix}-project-description`} path="description" element={<ProjectDescriptionContainer />} />
				<Route id={`${prefix}-project-activities`} path="activities" element={<ProjectActivitiesContainer />} />
				<Route id={`${prefix}-project-results`} path="results" element={<ProjectResultsContainer />} />
			</Route>
		</>
	);

	// Define protected-only routes
	const protectedOnlyRoutes = (
		<>
			<Route id="protected-home" path="/" element={<SelectProject />} />
			<Route id="protected-activity" path="activity/:activityId" element={<EditActivityContainer />}>
				<Route id="protected-activity-edit" path="edit" element={<EditActivityCombined />} />
			</Route>
		<Route id="project" path="project/:projectId" element={<ProjectContainer />}>
			<Route id="protected-project-new-activity" path="new-activity" element={<SetupAssistantContainer />} />
			<Route id="protected-project-new-biodiversity-activity" path="new-biodiversity-activity" element={<NewBiodiversityActivityPage />} />
		</Route>
			<Route id="protected-help" path="help" element={<HelpCenter />}>
				<Route id="protected-help-index" index element={<HelpCenterHome />} />
				<Route id="protected-help-faqs" path="faqs" element={<HelpCenterFaqs />} />
				<Route id="protected-help-training" path="training" element={<HelpCenterTraining />} />
				<Route id="protected-help-publications" path="publications" element={<HelpCenterPublications />} />
			</Route>
		</>
	);

	const router = createBrowserRouter(
		createRoutesFromElements(
			<Route element={<Root />}>
				<Route path="auth" element={<AuthLayout />}>
					<Route path="login" element={<Login />} />
					<Route path="register" element={<Register />} />
					<Route
						path="terms-and-conditions/:tab"
						element={<TermAndConditions />}
					/>
					<Route path="reset-password" element={<ResetPassword />} />
				</Route>

				<Route path="hih-investment-forum" element={<HihInvestmentForum />} />
				<Route path="hih-investment-forum/:isoCode" element={<HihInvestmentForum />} />

				{/* Protected routes */}
				<Route element={<ProtectedLayout />}>
					{protectedOnlyRoutes}
					{createSharedRoutes('protected')}
				</Route>

				{/* Public routes */}
				<Route element={<PublicLayout />}>
					<Route path="public" element={<RouteOutlet />}>
						{createSharedRoutes('public')}
					</Route>
				</Route>

				<Route path="*" element={<NoMatch404 />} />
			</Route>
		)
	);

	return <RouterProvider router={router} />;
}

// Helper function to get module component by module name
function getModuleComponent(module: FEModules) {
	switch (module) {
		case FEModules.AnnualCropland: return <AnnualCroplandModule />;
		case FEModules.LandUseChange: return <LandUseChangeModule />;
		case FEModules.LargeFisheries: return <LargeFisheriesModule />;
		case FEModules.SmallFisheries: return <SmallFisheriesModule />;
		case FEModules.Grassland: return <GrasslandManagementModule />;
		case FEModules.PerennialCropland: return <PerennialCropland />;
		case FEModules.LivestockManagement: return <Livestock />;
		case FEModules.FloodedRice: return <FloodedRice />;
		case FEModules.Aquaculture: return <AquacultureModule />;
		case FEModules.Inputs: return <InputsModule />;
		case FEModules.ForestManagement: return <ForestManagement />;
		case FEModules.Waterbodies: return <Waterbodies />;
		case FEModules.SetAside: return <SetAside />;
		case FEModules.OtherLand: return <OtherLand />;
		case FEModules.Energy: return <Energy />;
		case FEModules.Settlements: return <Settlements />;
		case FEModules.Irrigation: return <Irrigation />;
		case FEModules.CoastalWetlands: return <CoastalWetlands />;
		case FEModules.OrganicSoil: return <OrganicSoil />;
		case FEModules.ActivityComplete: return <ActivityCompleteStep />;
		case FEModules.Packaging: return <Packaging />;
		case FEModules.Transport: return <Transport />;
		case FEModules.Storage: return <Storages />;
		case FEModules.Processing: return <Processing />;
		default: return null;
	}
}

const Root = () => {
	const { isHeaderSmall } = useTabsHook();
	useScreenViewEventsHandler();
	useSetFirebaseUserProperties();
	useMsaEventSubscription();
	useLandCoverChangeAutoTrigger();
	useMapSaveStatusPolling();

	return (
		<AppProvider>
			<div className="page-container">
				<div className="content-wrap">
					<div className={`App ${!isHeaderSmall ? "App-mw" : ""}`}>
						<Header />
						<Provider>
							<ErrorBoundary>
								<Outlet />
							</ErrorBoundary>
						</Provider>
					</div>
					<Footer />
				</div>
			</div>
		</AppProvider>
	);
};

export default function WrappedApp() {
	const { initializeFirebase } = useFirebaseHook();
	useEffect(() => {
		initializeFirebase()
	}, [initializeFirebase])

	return (
		<Suspense fallback={<LoadingFallback />}>
			<App />
		</Suspense>
	)
}